//* Third Party Libraries
import * as React from "react";
import { isKnownRoute } from "next-known-route";

/**
 * Checks if the hostname of a URL matches valid configurations.
 * @param {string} hostname - The hostname extracted from the URL.
 * @param {Object} publicConfig - The configuration object containing host, apiHost, and proxyApiHost.
 * @returns {boolean} `true` if the hostname matches valid configurations; otherwise, `false`.
 */
const isHostnameValid = (hostname, publicConfig) => {
  const { host, apiHost, proxyApiHost = "" } = publicConfig;

  const apiHostWithoutProtocol = apiHost.replace("https://", "").split(":")[0];
  const proxyApiHostWithoutProtocol = proxyApiHost.replace("https://", "");

  return (
    !hostname ||
    [host, apiHostWithoutProtocol, proxyApiHostWithoutProtocol].includes(
      hostname
    )
  );
};

/**
 * Checks if a given pathname matches a known route or links in the publicConfig Object.
 * @param {string} pathname - The pathname extracted from the URL.
 * @param {Object} publicConfig - The configuration object
 * @returns {boolean} `true` if the path is a known route or matches cart links; otherwise, `false`.
 */
const isPathKnown = (pathname, publicConfig) => {
  return (
    typeof pathname === "string" &&
    (isKnownRoute(pathname) || publicConfig?.links?.cart === pathname)
  );
};

/**
 * Checks if a given pathname matches excluded paths.
 * @param {string} pathname - The pathname extracted from the URL.
 * @returns {boolean} `true` if the pathname matches any excluded paths; otherwise, `false`.
 */
const isExcludedPath = (pathname) => {
  const excludedPaths = [
    /\/story\/(holiday-gift-guide|idees-cadeaux|run-further|gifting-quiz)/,
    /\/help\/(track-order|returns|return-details|purchase-details|quality-promise)/,
    /^\/?$/
  ];

  return excludedPaths.some((regex) => regex.test(pathname));
};

/**
 * Determines if a given URL matches a known route based on hostname and path configurations.
 * @param {string} url - The URL to evaluate for routing and exclusions.
 * @param {Object} publicConfig - The configuration object containing host, apiHost, proxyApiHost, and links.
 * @returns {Object} An object containing:
 *   - {boolean} routeMatch - Indicates whether the URL matches a known route.
 *   - {string} relativeURL - The relative portion of the URL for use in internal links.
 */
export const doesMatchRoute = (url, publicConfig) => {
  const prefixedUrl = url.startsWith("//") ? `https:${url}` : url;
  let parsedUrl;

  try {
    parsedUrl = new URL(prefixedUrl);
  } catch (e) {
    parsedUrl = { pathname: prefixedUrl, search: "", hostname: "" };
  }

  const { pathname, search, hostname } = parsedUrl;

  const routeMatch =
    isHostnameValid(hostname, publicConfig) &&
    isPathKnown(pathname, publicConfig) &&
    !isExcludedPath(pathname);

  const relativeURL = `${pathname}${search}`;
  return { routeMatch, relativeURL };
};

/**
 * Prepends the i18n locale to a relative URL if necessary.
 * This function ensures server-side rendered URLs include the locale for SEO purposes.
 * Locales such as `en-us` and `fr-ca` are ignored unless specified in the configuration.
 * @param {Object} options - Options for constructing the localized URL.
 * @param {string} options.locale - The i18n locale from the Next.js router (e.g., "en-ca").
 * @param {string} options.relativeURL - The relative URL to prepend with the locale.
 * @param {boolean} [options.frSubDomainMigrationHdrFlag=false] - Flag to toggle subdomain-based localization behavior.
 * @returns {string} The relative URL with the locale prepended if applicable.
 *
 * @example
 * const localizedURL = prependI18nLocale({
 *   locale: "en-ca",
 *   relativeURL: "/c/womens-leggings",
 * });
 * console.log(localizedURL); // "/en-ca/c/womens-leggings"
 */
export const prependI18nLocale = ({
  locale = "en-us",
  relativeURL,
  frSubDomainMigrationHdrFlag = false
}) => {
  const ignoreLocales = frSubDomainMigrationHdrFlag
    ? ["en-us"]
    : ["en-us", "fr-ca"];

  if (ignoreLocales.includes(locale) || relativeURL.includes(locale)) {
    return relativeURL;
  }

  return `/${locale}${relativeURL}`;
};

/**
 * Checks if a React element has valid children.
 * @param {React.ReactNode} element - The React element to check.
 * @returns {boolean} True if the element has valid children, false otherwise.
 */
export function hasChildren(element) {
  return React.isValidElement(element) && Boolean(element.props.children);
}

/**
 * Converts a child node to a string representation.
 * @param {React.ReactNode} child - The child node to convert.
 * @returns {string} The string representation of the child node.
 */
export function childToString(child) {
  if (
    typeof child === "undefined" ||
    child === null ||
    typeof child === "boolean"
  ) {
    return "";
  }

  if (typeof child === "object") {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      // Handle React.Fragment specifically
      return onlyText(child.props.children);
    }
    return "";
  }

  return child.toString();
}

/**
 * Extracts all text content from React children, including nested structures.
 * @param {React.ReactNode} children - The React children to process.
 * @returns {string} The concatenated text content of the children.
 */
export function onlyText(children) {
  if (!(children instanceof Array) && !React.isValidElement(children)) {
    return childToString(children);
  }

  return React.Children.toArray(children).reduce((text, child) => {
    let newText;

    if (React.isValidElement(child) && hasChildren(child)) {
      newText = onlyText(child.props.children);
    } else {
      newText = childToString(child);
    }

    return text.concat(newText);
  }, "");
}
